import '@material/web/icon/icon.js'
import '@material/web/textfield/filled-text-field.js'
import '@material/web/select/outlined-select'
import '@material/web/button/elevated-button.js'
import '@material/web/chips/chip-set.js'
import '@material/web/chips/assist-chip.js'
import '@material/web/chips/filter-chip.js'

import gql from 'graphql-tag'
import { css, html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { connect } from 'pwa-helpers/connect-mixin.js'

import { i18next } from '@operato/i18n'
import { client } from '@operato/graphql'
import { navigate, store } from '@operato/shell'
import { OxPrompt } from '@operato/popup/ox-prompt.js'

import { styles as MDTypeScaleStyles } from '@material/web/typography/md-typescale-styles'

@customElement('board-basic-info')
export class BoardInfo extends connect(store)(LitElement) {
  static styles = [
    MDTypeScaleStyles,
    css`
      :host {
        position: relative;
        display: block;
        background-color: var(--md-sys-color-surface);
        color: var(--md-sys-color-on-surface);

        --md-filled-field-top-space: 12px;
        --md-filled-field-bottom-space: 12px;
        --md-filled-field-leading-space: 12px;
        --md-filled-field-trailing-space: 12px;

        --md-outlined-field-top-space: 12px;
        --md-outlined-field-bottom-space: 12px;
        --md-outlined-field-leading-space: 12px;
        --md-outlined-field-trailing-space: 12px;
      }

      img {
        object-fit: contain;
        max-width: 100%;
        max-height: 100%;
        border-bottom: 2px solid rgba(0, 0, 0, 0.1);
      }

      form {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        grid-gap: 10px 0;
        grid-auto-rows: minmax(24px, auto);
        padding: var(--spacing-large);
        align-items: center;
      }

      md-outlined-select,
      md-filled-text-field {
        flex: 1;
        padding: 4px 0;
        min-width: unset;
      }

      md-chip-set {
        flex: 1;
        display: flex;
        gap: 4px;
      }

      fieldset {
        display: contents;
      }

      legend {
        grid-column: span 12;
        padding: var(--legend-padding);
        color: var(--md-sys-color-primary);
        text-transform: capitalize;
      }

      label {
        text-transform: capitalize;
      }

      md-icon[dim] {
        font-variation-settings: 'FILL' 1;
        --md-icon-size: 14px;
        color: var(--md-sys-color-outline-variant);
      }

      [name] {
        max-width: 70px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      [danger] {
        --md-assist-chip-elevated-container-color: var(--md-sys-color-error);
        --md-assist-chip-label-text-color: var(--md-sys-color-on-error);
        --md-assist-chip-leading-icon-color: var(--md-sys-color-on-error);
      }

      [fullwidth] {
        grid-column: span 12 / auto;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      [oneline] {
        grid-column: span 12 / auto;
        display: flex;
        flex-direction: row;
        gap: 4px;
        align-items: center;
      }

      [oneline] md-filled-text-field,
      [oneline] select {
        flex: 1;
      }

      [right] {
        margin-left: auto;
      }

      @media screen and (max-width: 460px) {
        :host {
          width: 100vw;
        }
      }
    `
  ]

  @property({ type: String }) boardId?: string

  @state() board: any
  @state() showTarget: boolean = false
  @state() subdomain?: string
  @state() enableModeller?: boolean = false

  @state() targetDomains: { id: string; name: string; subdomain: string }[] = []
  @state() targetSubdomain?: string

  private original: any

  render() {
    var board = this.board || { name: '', description: '' }

    return html`
      ${board.thumbnail ? html` <img src=${board.thumbnail} /> ` : html``}

      <form class="md-typescale-body-medium">
        <fieldset>
          <legend class="md-typescale-label-large-prominent">${i18next.t('label.information')}</legend>
          <div oneline>
            <md-filled-text-field
              label=${String(i18next.t('label.name'))}
              type="text"
              .value=${board.name}
              @change=${e => (this.board = { ...this.board, name: e.target.value })}
              ?disabled=${!this.enableModeller}
            ></md-filled-text-field>
          </div>

          <md-filled-text-field
            oneline
            label=${String(i18next.t('label.description'))}
            .value=${board.description}
            @change=${e => (this.board = { ...this.board, description: e.target.value })}
            ?disabled=${!this.enableModeller}
          ></md-filled-text-field>

          <md-chip-set oneline>
            <md-assist-chip danger elevated ?disabled=${!this.enableModeller} label=${String(i18next.t('button.delete'))} @click=${this.deleteBoard.bind(this)}>
              <md-icon slot="icon">delete_outline</md-icon>
            </md-assist-chip>

            <md-assist-chip elevated ?disabled=${!this.enableModeller} label=${String(i18next.t('button.save'))} @click=${this.updateBoard.bind(this)} right>
              <md-icon slot="icon">save</md-icon>
            </md-assist-chip>
            <md-assist-chip
              elevated
              ?disabled=${!this.enableModeller}
              label=${String(i18next.t('button.edit'))}
              @click=${() => navigate(`${'board-modeller/' + this.boardId}`)}
            >
              <md-icon slot="icon">drive_file_rename_outline</md-icon>
            </md-assist-chip>
          </md-chip-set>
        </fieldset>

        <fieldset>
          <legend class="md-typescale-label-large-prominent">${i18next.t('label.status')}</legend>
          <div oneline>
            <label class="md-typescale-label-meium">${i18next.t('label.created-at')}</label>
            <md-icon dim>schedule</md-icon> ${new Date(board.createdAt).toLocaleString()}
          </div>

          <div oneline>
            <label class="md-typescale-label-meium">${i18next.t('label.updated-at')}</label>
            <md-icon dim>schedule</md-icon> ${new Date(board.updatedAt).toLocaleString()}
          </div>
        </fieldset>
      </form>
    `
  }

  stateChanged(state) {
    this.subdomain = state.app?.domain?.subdomain
    this.targetSubdomain = this.targetSubdomain ?? this.subdomain
  }

  async firstUpdated() {
    this.enableModeller = true

    this.refresh()
  }

  async refresh() {
    var response = (
      await client.query({
        query: gql`
          query FetchBoardById($id: String!) {
            board(id: $id) {
              id
              name
              description
              model
              thumbnail
              createdAt
              creator {
                id
                name
              }
              state
              version
              updatedAt
              updater {
                id
                name
              }
            }

            domainsWithPrivilege(privilege: "mutation", category: "board") {
              name
              subdomain
            }
          }
        `,
        variables: { id: this.boardId }
      })
    ).data

    this.board = response.board

    this.original = { ...this.board }

    this.targetDomains = response.domainsWithPrivilege
  }

  async updateBoard() {
    const { id, name, description } = this.board
    /* model 정보를 제외하고 업데이트 */

    this.dispatchEvent(
      new CustomEvent('update-board', {
        detail: { id, name, description },
        bubbles: true,
        composed: true
      })
    )
  }

  async deleteBoard() {
    if (
      await OxPrompt.open({
        title: i18next.t('text.are_you_sure'),
        text: i18next.t('text.sure_to_x', { x: i18next.t('text.delete') }),
        confirmButton: { text: i18next.t('button.confirm') },
        cancelButton: { text: i18next.t('button.cancel') }
      })
    ) {
      this.dispatchEvent(
        new CustomEvent('delete-board', {
          detail: this.board,
          bubbles: true,
          composed: true
        })
      )

      this.close()
    }
  }

  close() {
    history.back()
  }
}
